import { Product, BusinessInfo } from './barcode.service';

export const CART_STORAGE_KEY = 'haat_product_cart';
const BUSINESS_STORAGE_KEY = 'haat_business_info_v2';
export const MAX_CART_ITEMS = 100;

// Extended cart item for manual entries (products without full data)
export interface ManualCartItem {
    id: string;
    type: 'scanned' | 'manual';
    // For scanned products
    product?: Product;
    rowIndex?: number;
    // For manual entries
    manualData?: {
        name: string;
        barcode?: string;
        category?: string;
        subCategory?: string;
        categoryNames?: {
            ar?: string;
            he?: string;
            en?: string;
        };
        subCategoryNames?: {
            ar?: string;
            he?: string;
            en?: string;
        };
        imageURL?: string;
        // Kept so a weighable product stays weighable when the full catalog
        // row could not be hydrated and we fall back to a manual entry.
        quantityType?: string;
        avgWeight?: string;
        weightToPresent?: string;
    };
    // Common fields
    price: string;
    availability: 'TRUE' | 'FALSE';
    notes?: string;
    // Catalog average item weight as it arrived from Hub. The operator value
    // overwrites `product.avgWeight` on the way in, so the original is kept
    // here to show what was changed.
    catalogAvgWeight?: string;
    addedAt: number;
}

/**
 * Editable cart fields. `avgWeight` is stored on the product row (scanned) or
 * on `manualData` (manual), so it is patched separately from the flat fields.
 */
export type CartItemUpdate = Partial<
    Pick<ManualCartItem, 'price' | 'availability' | 'notes'>
> & { avgWeight?: string };

export type CartAddPreflightResult =
    | { status: 'ready' }
    | { status: 'duplicate'; item: ManualCartItem }
    | { status: 'full' };

export type CartAddResult =
    | (ManualCartItem & { status: 'added'; wasAdded: true })
    | (ManualCartItem & { status: 'duplicate'; wasAdded: false })
    | { status: 'full'; wasAdded: false };

class CartService {
    /** In-memory mirror of server draft cart (synced by CartContext). */
    private memoryItems: ManualCartItem[] = [];

    private normalizeBarcode(barcode?: string | null): string {
        return String(barcode || '').trim();
    }

    private getNormalizedItemBarcode(item: ManualCartItem): string {
        if (item.type === 'scanned' && item.product) {
            return this.normalizeBarcode(item.product.barcode);
        }
        return this.normalizeBarcode(item.manualData?.barcode);
    }

    private dedupeItemsByBarcode(items: ManualCartItem[]): ManualCartItem[] {
        const seenBarcodes = new Set<string>();

        return items.filter(item => {
            const barcode = this.getNormalizedItemBarcode(item);
            if (!barcode) return true;
            if (seenBarcodes.has(barcode)) return false;
            seenBarcodes.add(barcode);
            return true;
        });
    }

    private findItemByBarcode(items: ManualCartItem[], barcode?: string | null): ManualCartItem | undefined {
        const normalizedBarcode = this.normalizeBarcode(barcode);
        if (!normalizedBarcode) return undefined;
        return items.find(item => this.getNormalizedItemBarcode(item) === normalizedBarcode);
    }

    setMemoryItems(items: ManualCartItem[]): void {
        this.memoryItems = this.dedupeItemsByBarcode(items);
    }

    getCartItems(): ManualCartItem[] {
        return this.memoryItems;
    }

    /** One-time localStorage cart used only for migration to the server. */
    readLegacyLocalCart(): ManualCartItem[] {
        try {
            const stored = localStorage.getItem(CART_STORAGE_KEY);
            if (!stored) return [];
            const parsed = JSON.parse(stored);
            if (!Array.isArray(parsed)) return [];
            return this.dedupeItemsByBarcode(parsed as ManualCartItem[]);
        } catch (error) {
            console.error('Error loading legacy cart from localStorage:', error);
            return [];
        }
    }

    clearLegacyLocalCart(): void {
        localStorage.removeItem(CART_STORAGE_KEY);
    }

    preflightAddByBarcode(barcode?: string | null): CartAddPreflightResult {
        const items = this.getCartItems();
        const existingItem = this.findItemByBarcode(items, barcode);
        if (existingItem) return { status: 'duplicate', item: existingItem };
        if (items.length >= MAX_CART_ITEMS) return { status: 'full' };
        return { status: 'ready' };
    }

    getCartCount(): number {
        return this.getCartItems().length;
    }

    getItemName(item: ManualCartItem): string {
        if (item.type === 'scanned' && item.product) {
            return item.product.prodNameHe || item.product.prodNameAr || item.product.barcode || 'מוצר';
        }
        return item.manualData?.name || 'מוצר';
    }

    getItemImage(item: ManualCartItem): string | undefined {
        if (item.type === 'scanned' && item.product) {
            return item.product.imageURL || item.product.smallImageUrl;
        }
        return item.manualData?.imageURL;
    }

    getItemBarcode(item: ManualCartItem): string | undefined {
        const barcode = this.getNormalizedItemBarcode(item);
        return barcode || undefined;
    }

    isProductInCart(barcode: string): boolean {
        return Boolean(this.findItemByBarcode(this.getCartItems(), barcode));
    }

    saveBusinessInfo(info: BusinessInfo): void {
        try {
            localStorage.setItem(BUSINESS_STORAGE_KEY, JSON.stringify(info));
        } catch (error) {
            console.error('Error saving business info:', error);
        }
    }

    getBusinessInfo(): BusinessInfo | null {
        try {
            const stored = localStorage.getItem(BUSINESS_STORAGE_KEY);
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (error) {
            console.error('Error loading business info:', error);
        }
        return null;
    }

    clearBusinessInfo(): void {
        localStorage.removeItem(BUSINESS_STORAGE_KEY);
    }

    clearLegacyAuthSession(): void {
        localStorage.removeItem('haat_auth_session');
    }

    isOtpAuthenticated(): boolean {
        const businessInfo = this.getBusinessInfo();
        return Boolean(
            businessInfo &&
            typeof businessInfo.haatBussID === 'string' &&
            businessInfo.haatBussID.trim() &&
            typeof businessInfo.businessName === 'string' &&
            businessInfo.businessName.trim() &&
            typeof businessInfo.phone === 'string' &&
            businessInfo.phone.trim()
        );
    }
}

export const cartService = new CartService();
